import { Injectable } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { VirtualCompetitionPrefix } from '@frontend/sports/common/core/data-access/sport-model';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';

import { BreadcrumbModel } from '../../breadcrumbs/breadcrumb.models';
import { BreadcrumbsResolve } from '../../breadcrumbs/breadcrumbs.resolver';
import { BetBuilderDetail } from '../../event-list-shared/coupons/bet-builder/bet-builder-detail.model';
import { CouponDetail } from '../../event-list-shared/coupons/coupon.model';
import { CouponUrlParam, SportUrlParam, UrlHelperService } from '../../navigation-core/url-helper.service';

const competitionIcon = 'sports-icon sports-league';
@Injectable()
export class CouponBreadcrumbsResolver extends BreadcrumbsResolve<CouponDetail> {
    constructor(
        private sitecore: Sitecore,
        private urlHelper: UrlHelperService,
    ) {
        super();
    }

    protected customResolve(coupon: CouponDetail): BreadcrumbModel[] {
        if (!coupon?.sport) {
            return [];
        }

        const sport = coupon.sport;
        const title = this.sitecore.seo.SportBreadcrumbTitle.replace(/{{sport}}/, sport.name);
        let crumb = this.createCrumb({
            icon: 'theme-coupons-all',
            title: this.sitecore.globalMessages.Coupons,
            url: this.urlHelper.getCouponUrl(sport),
        });
        let region: UrlParam | undefined;
        let league: UrlParam | undefined;

        if ((coupon.competition || coupon.virtualCompetitionData) && coupon.page.fixtures.length) {
            const fixture = coupon.page.fixtures[0];
            region = {
                id: fixture.region.id,
                name: fixture.region.name.value,
            };
            league = {
                id: coupon.virtualCompetitionData?.id || fixture.competition.id,
                name: coupon.virtualCompetitionData?.name ?? fixture.competition.name.value,
                prefix: coupon.virtualCompetitionData ? VirtualCompetitionPrefix : undefined,
            };

            const competitionTitle = this.sitecore.seo.LeagueHeadingTitle.replace(/{{league}}/, league.name);
            crumb = this.createCrumb({
                icon: competitionIcon,
                title: competitionTitle,
                url: this.urlHelper.getSportUrl(sport, SportUrlParam.Betting, region, league),
            });
        }

        return [
            this.createCrumb({
                icon: `sports-${sport.id}`,
                title,
                url: this.urlHelper.getSportUrl(sport),
            }),
            crumb,
            this.createCrumb({
                icon: 'sports-coupons-icon',
                title: coupon.title,
                url: this.urlHelper.getCompetitionCouponUrl(
                    sport,
                    {
                        id: coupon.id,
                        name: coupon.title,
                    },
                    region,
                    league,
                ),
            }),
        ];
    }
}

@Injectable()
export class BetbuilderBreadcrumbsResolver extends BreadcrumbsResolve<BetBuilderDetail> {
    constructor(
        private sitecore: Sitecore,
        private urlHelper: UrlHelperService,
    ) {
        super();
    }

    protected customResolve(data: BetBuilderDetail): BreadcrumbModel[] {
        if (!data?.fixture?.fixtures.length) {
            return [];
        }

        const fixture = data.fixture.fixtures[0];
        const sport = {
            id: fixture.sport.id,
            name: fixture.sport.name.value,
        };

        const title = this.sitecore.seo.SportBreadcrumbTitle.replace(/{{sport}}/, sport.name);

        let crumb = this.createCrumb({
            icon: 'theme-coupons-all',
            title: this.sitecore.globalMessages.Coupons,
            url: this.urlHelper.getCouponUrl(sport),
        });
        let region: UrlParam | undefined;
        let league: UrlParam | undefined;

        if (data.competitionId) {
            region = {
                id: fixture.region.id,
                name: fixture.region.name.value,
            };
            league = {
                id: fixture.competition.id,
                name: fixture.competition.name.value,
            };

            const competitionTitle = this.sitecore.seo.LeagueHeadingTitle.replace(/{{league}}/, league.name);
            crumb = this.createCrumb({
                icon: competitionIcon,
                title: competitionTitle,
                url: this.urlHelper.getSportUrl(sport, SportUrlParam.Betting, region, league),
            });
        }

        return [
            this.createCrumb({
                icon: `sports-${sport.id}`,
                title,
                url: this.urlHelper.getSportUrl(sport),
            }),
            crumb,
            this.createCrumb({
                icon: 'sports-coupons-icon',
                title: this.sitecore.betBuilder.messages?.BetBuilderEventsTitle ?? '',
                url: this.urlHelper.getCompetitionCouponUrl(sport, CouponUrlParam.BetBuilder, region, league),
            }),
        ];
    }
}
